import { memo, useCallback } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens, Button } from '@coreai/fluentui-react';
import { ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';

const MAX_VISIBLE_COUNT = 99;

const useStyles = makeStyles({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    minWidth: 'auto',
    height: '24px',
    padding: `0 ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    color: tokens.colorNeutralForeground2,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
    },
    '@media (forced-colors: active)': {
      ...shorthands.border('1px', 'solid', 'ButtonText'),
    },
  },
  count: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1',
    paddingRight: tokens.spacingHorizontalXXS,
  },
  icon: {
    fontSize: tokens.fontSizeBase300,
  },
});

export interface CollapseChipProps {
  /** True when the node is collapsed (children hidden). */
  collapsed: boolean;
  /** Number of direct children that collapsing hides. */
  count: number;
  /** Toggle handler — receives the next collapsed state. */
  onToggle: (nextCollapsed: boolean) => void;
  /** Accessible noun for the hidden children, singular/plural. */
  nounSingular?: string;
  nounPlural?: string;
  className?: string;
}

/**
 * A small count-and-chevron pill that toggles a node's collapse state.
 *
 * - Collapsed: shows the direct child count (e.g. "3") plus a right chevron.
 * - Expanded: shows just a down chevron (children are already visible).
 *
 * The visible count is always part of the accessible name (WCAG 2.5.3). The
 * chip is a drag-exempt (`nodrag`) control and stops propagation so clicking it
 * never selects/drags the underlying node.
 */
export const CollapseChip = memo(
  ({
    collapsed,
    count,
    onToggle,
    nounSingular = 'item',
    nounPlural = 'items',
    className,
  }: CollapseChipProps) => {
    const styles = useStyles();

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggle(!collapsed);
      },
      [collapsed, onToggle]
    );

    const noun = count === 1 ? nounSingular : nounPlural;
    const displayCount = count > MAX_VISIBLE_COUNT ? `${MAX_VISIBLE_COUNT}+` : String(count);
    const ariaLabel = collapsed
      ? `Show ${count} ${noun}`
      : `Hide ${count} ${noun}`;

    return (
      <Button
        appearance="subtle"
        className={mergeClasses(styles.chip, 'nodrag', className)}
        onClick={handleClick}
        aria-expanded={!collapsed}
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        {collapsed && <span className={styles.count}>{displayCount}</span>}
        {collapsed ? (
          <ChevronRightRegular className={styles.icon} />
        ) : (
          <ChevronDownRegular className={styles.icon} />
        )}
      </Button>
    );
  }
);

CollapseChip.displayName = 'CollapseChip';
